/**
 * EL CAMPO DEL PESO («Todo lo validado antes del 1 de septiembre», bloque 4: «El peso. Un
 * solo registro, tres puertas»).
 *
 *     «Abierto todo el año. Es el único sitio donde el peso se escribe.»
 *
 * Antes se escribía DENTRO del cierre del día, al final de once preguntas. Con eso el que
 * no cerraba el día no tenía dónde apuntarlo -- y son la mayoría --, y el que lo apuntaba
 * dejaba el dato enterrado en un formulario que no vuelve a abrir. Ahora vive aquí, en
 * Evolución, que es de donde sale la curva, y las otras dos puertas del bloque 4 traen
 * hasta aquí: la fila «Hoy toca pesarte» de Inicio los días de pesada y el paso 1 del
 * reporte quincenal.
 *
 * LA FECHA DEL PESAJE SIGUE VIVA (punto 34 del doc 24-08). No sale en la maqueta porque la
 * maqueta enseña el campo en reposo, vacío, y en reposo tampoco salía en el cierre: aparece
 * al escribir un peso, y solo si hay más de un día al que fecharlo. Sin ella, el que se
 * pesa por la mañana y lo anota de noche rompe la pareja de días seguidos de la que sale la
 * media semanal.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { revisarPeso, PESO_MIN, PESO_MAX, kg } from '../lib/pesoValido';
import { useConfirm } from './ui/confirm';
import { mensajeDeError } from '../lib/mensajeDeError';

// El día de HOY es el del navegador, no el del servidor: el día vivido es el suyo.
const hoyDelCliente = () => new Date().toLocaleDateString('en-CA');

// Los días a los que puede fechar un pesaje: hoy y los `diasAtras` anteriores. El número lo
// dice el servidor (`peso_dias_atras`), que es el que luego lo acepta o no.
export const diasParaFechar = (hoyIso, diasAtras = 14) => {
    const dias = [];
    for (let i = 0; i <= diasAtras; i++) {
        const d = new Date(`${hoyIso}T12:00:00`);
        d.setDate(d.getDate() - i);
        const etiqueta = i === 0 ? 'Hoy' : i === 1 ? 'Ayer'
            : d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        dias.push({
            iso: d.toLocaleDateString('en-CA'),
            etiqueta: etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1),
        });
    }
    return dias;
};

const cuando = (iso) => {
    if (!iso) return '';
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

/**
 * @param ultimo   {valor, fecha} del último pesaje conocido, para la línea de abajo y para
 *                 medir el salto que canta.
 * @param abrir    llega con el foco puesto: es el que viene de «Hoy toca pesarte», que
 *                 espera escribir sin buscar nada.
 */
const CampoDePeso = ({ api, ultimo = null, diasAtras = 14, abrir = false, onGuardado,
                       pesoDelDia = null, pesoSemanal = null, detalleSemanal = null,
                       conLosDosNumeros = true }) => {
    const [valor, setValor] = useState('');
    const [fecha, setFecha] = useState(hoyDelCliente);
    const [guardando, setGuardando] = useState(false);
    const { confirm } = useConfirm();
    const campo = React.useRef(null);

    useEffect(() => { if (abrir) campo.current?.focus(); }, [abrir]);

    // ¿HOY TOCA PESARSE? (punto 30 de Jesús: «Inicio le dice "hoy toca pesarte" y el campo
    // le dice "registrarlo es opcional"»). Las dos frases son verdad por separado y juntas
    // se contradicen delante del cliente.
    //
    // Se pregunta al MISMO sitio que pregunta Inicio (`/checkins/estado`), no a una regla
    // copiada aquí: dos reglas paralelas es como nacen estas contradicciones. Si la llamada
    // falla se queda en «no toca», que es la frase de siempre y la que no promete nada.
    const [tocaPesarse, setTocaPesarse] = useState(false);
    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const r = await api.get(
                    `/checkins/estado?fecha=${hoyDelCliente()}&hora_cliente=${new Date().getHours()}`);
                if (vivo) setTocaPesarse(!!r?.data?.toca_pesarse);
            } catch (e) {
                // Sin respuesta, la frase de siempre. No se le dice nada al cliente: esto no
                // es un fallo suyo ni le impide escribir su peso.
            }
        })();
        return () => { vivo = false; };
    }, [api]);

    const dias = diasParaFechar(hoyDelCliente(), diasAtras);

    const guardar = async () => {
        const chequeo = revisarPeso(valor, ultimo?.valor);
        if (!chequeo.ok) return toast.error(chequeo.error);
        if (chequeo.confirmar) {
            const sigue = await confirm({
                title: 'Confírmame el peso',
                description: chequeo.confirmar,
                confirmLabel: 'Sí, es correcto', cancelLabel: 'Lo corrijo',
            });
            if (!sigue) return;
        }
        setGuardando(true);
        try {
            const r = await api.post('/clients/me/peso', { valor: chequeo.peso, fecha });
            // LO QUE DIGA EL SERVIDOR, NO LO QUE HEMOS MANDADO (10-09).
            //
            // Desde la regla de Jesús del 8-09 -- «una pesada por día, y si se pesa dos veces
            // el mismo miércoles cuenta la primera» -- hay un caso en que el peso NO se
            // guarda. Y aquí se decía «Peso guardado» igual: el cliente escribía una
            // corrección, leía que se había guardado y el número de la tarjeta no se movía.
            // Eso no es un detalle de texto; es la app diciéndole que tiene su dato cuando
            // no lo tiene.
            const yaHabia = r?.data?.ya_habia;
            if (r?.data?.guardado === false && yaHabia != null) {
                // SIN TILDES DE LAS QUE SE FUERON (Jesús, 11-09). «Sólo» y «ése» ya no la
                // llevan, y aquí salían las dos a dos líneas de un «Solo necesitamos dos
                // pesadas» bien escrito, en la misma pantalla.
                toast.info(`Ya habías apuntado ${kg(yaHabia)} kg ese día y se queda ese. `
                    + 'Solo contamos la primera pesada de cada día.');
            } else {
                toast.success('Peso guardado');
            }
            setValor('');
            setFecha(hoyDelCliente());
            onGuardado?.();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo guardar el peso'));
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div data-testid="campo-de-peso">
            {/* SIN LA ETIQUETA «SIEMPRE ABIERTO» (Jesús, 8-09: «la etiqueta sobra»). Decía
                en verde que el campo está disponible, que es justo lo que ya se ve: el
                campo está ahí y se puede escribir en él. */}
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Tu peso
                </p>
            </div>
            <div className="flex gap-2">
                <div className="flex-1 bg-muted rounded-xl px-3 py-2.5 flex items-center justify-between">
                    <input ref={campo} type="number" step="0.1" min={PESO_MIN} max={PESO_MAX}
                        value={valor} onChange={e => setValor(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') guardar(); }}
                        data-testid="peso-valor" placeholder="0,0" aria-label="Tu peso en kilos"
                        className="w-full bg-transparent border-0 p-0 text-sm text-foreground
                            placeholder:text-muted-foreground focus:outline-none" />
                    <span className="text-xs text-muted-foreground font-data pl-2">kg</span>
                </div>
                <button onClick={guardar} disabled={guardando || !valor}
                    data-testid="guardar-peso"
                    className="btn-brand px-5 rounded-xl text-sm font-bold disabled:opacity-60">
                    {guardando ? 'Guardando…' : 'Guardar'}
                </button>
            </div>

            {valor && dias.length > 1 && (
                <div className="mt-2">
                    <label className="text-[11px] text-muted-foreground block mb-1" htmlFor="peso-fecha">
                        ¿De qué día es este peso?
                    </label>
                    <select id="peso-fecha" value={fecha} onChange={e => setFecha(e.target.value)}
                        data-testid="peso-fecha"
                        className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground">
                        {dias.map(d => <option key={d.iso} value={d.iso}>{d.etiqueta}</option>)}
                    </select>
                </div>
            )}

            {/* EL TEXTO DE DEBAJO, EL SUYO (Jesús, 8-09). Las dos frases que había se van
                las dos, y él dice por qué:

                  «Registrarlo es opcional, sólo para ti»  — no era verdad
                  «Te lo pediremos sólo para los reportes» — deja de serlo

                Ya no es opcional ni es sólo para el reporte: el peso pasa a ser un dato de
                la semana, y de dos pesadas sale su peso semanal. El texto lo dice y de paso
                dice que le avisaremos, que es la parte que la app ya hace (le pide pesarse
                el miércoles y el jueves hasta las 12:00).

                La frase de los días que toca se queda: es una instrucción para HOY y no
                compite con esto, que es la regla de siempre. */}
            {/* «HOY TOCA» SE SUMA, NO SUSTITUYE (10-09).
                Estaba con un `?:` y los miércoles y jueves antes de las 12 -- justo los días
                de su ventana -- el texto de Jesús NO SE VEÍA. El `?:` se puso el 5-09 para
                su punto 30: «Inicio le dice "hoy toca pesarte" y el campo le dice
                "registrarlo es opcional"», dos frases que juntas se contradicen. Pero esa
                segunda frase la quita él mismo en el bloque A del 8-09, así que la
                contradicción ya no existe y con ella se va el motivo de esconder nada.
                Manda lo más reciente: su texto va siempre, y la instrucción del día encima. */}
            {tocaPesarse && (
                <p className="text-[11px] text-foreground font-medium mt-2 leading-snug"
                   data-testid="peso-hoy-toca">
                    Hoy toca: en ayunas y después de ir al baño.
                </p>
            )}
            <p className="text-[11px] text-muted-foreground mt-2 leading-snug"
                data-testid="peso-cuando">
                Puedes apuntarlo a diario, pero no hace falta. Solo necesitamos dos pesadas (te avisamos). La media es tu peso semanal.
            </p>
            {/* LOS DOS NÚMEROS (Jesús, 8-09): «el del día y el de la semana, con su último
                registro cada uno».
                  · el del día NO lleva fecha: es el de hoy. Si no se ha pesado, «Sin apuntar».
                  · el semanal SÍ, y con los días del mes, «porque puede ser de la semana
                    pasada y hay que saber de cuándo es ese número».
                  · y la cursiva de debajo SE QUEDA SIEMPRE, tenga datos o no: «cuando está
                    vacío es cuando más falta hace».
                Sustituye a la línea «Tu último peso: 80 kg · 9 de septiembre», que decía el
                último fuera de cuando fuera y no distinguía el día de la semana. */}
            {/* Y SOLO DONDE SE SABEN (13-09). Los dos numeros salen de quien monta el
                campo, y donde este es una PUERTA para escribir -- el hito de «Mi semana»,
                el cierre del dia -- nadie se los pasa: se quedaban los dos en «Sin
                apuntar» tuviera el cliente pesadas o no, que es la app mintiendo en un
                sitio donde ademas no se venia a leer nada. Su casa es Evolucion, que es
                quien los tiene. */}
            {/* Y DONDE NO VAN LOS DOS, SU ULTIMO REGISTRO (Francisco, 13-09). Su maqueta
                del cierre lo pone asi: «Ultimo registro: 96 kg · 22 de julio». Es el dato
                que tiene quien monta esta puerta, y sin el la casilla se queda muda: no
                sabe si lo de hoy sube o baja. */}
            {!conLosDosNumeros && ultimo?.valor != null && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="peso-ultimo">
                    Último registro: {kg(ultimo.valor)} kg · {cuando(ultimo.fecha)}
                </p>
            )}
            {conLosDosNumeros && (
            <div className="mt-3 pt-3 border-t border-border space-y-1" data-testid="peso-dos-numeros">
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Tu peso del día</span>
                    <span className="text-sm font-data font-bold text-foreground" data-testid="peso-del-dia">
                        {pesoDelDia != null ? `${kg(pesoDelDia)} kg` : 'Sin apuntar'}
                    </span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">Tu peso semanal</span>
                    <span className="text-sm font-data font-bold text-foreground" data-testid="peso-semanal">
                        {pesoSemanal != null ? `${kg(pesoSemanal)} kg` : 'Sin apuntar'}
                    </span>
                </div>
                {detalleSemanal && (
                    <p className="text-[11px] text-muted-foreground/80 text-right leading-tight"
                       data-testid="peso-semanal-detalle">
                        {detalleSemanal}
                    </p>
                )}
                <p className="text-[11px] text-muted-foreground/70 italic leading-snug pt-1"
                   data-testid="peso-ventana">
                    Idealmente entre miércoles y viernes, para evitar rebotes del fin de semana.
                </p>
            </div>
            )}
        </div>
    );
};

export default CampoDePeso;
